import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Export configuration for better maintainability
const EXPORT_CONFIG = {
  // Fields to exclude from all exports
  EXCLUDED_FIELDS: ['_id', '__v', 'password', 'plan', 'refreshToken', 'verifyToken'],
  
  // Preferred column order for user exports (matching the table columns)
  USER_COLUMN_ORDER: ['name', 'email', 'role', 'status', 'workspaces', 'lastLogin', 'createdAt'],
  
  // Preferred column order for workspace exports - Updated for agency sub-workspace format
  WORKSPACE_COLUMN_ORDER: ['ID', 'Workspace', 'Owner', 'Plan', 'Status', 'Usage', 'Created'],

  // Fallback column order for backward compatibility
  WORKSPACE_COLUMN_ORDER_LEGACY: ['name', 'owner', 'ownerEmail', 'plan', 'status', 'memberCount', 'agentCount', 'createdAt'],
  
  // PDF specific configuration
  PDF: {
    MAX_COLUMNS: 10,
    MAX_STRING_LENGTH: 50,
    PRIORITY_FIELDS: ['firstName', 'lastName', 'email', 'role', 'status', 'workspaces', 'lastLogin', 'createdAt']
  },
  
  // Excel configuration
  EXCEL: {
    MAX_COL_WIDTH: 50,
    MIN_COL_WIDTH: 10
  }
};

// Helper to check if field should be excluded
const shouldExcludeField = (field) => {
  return EXPORT_CONFIG.EXCLUDED_FIELDS.includes(field);
};

// Helper function to flatten nested objects with better handling
const flattenObject = (obj, prefix = '', depth = 0, maxDepth = 3) => {
  const flattened = {};
  
  if (depth > maxDepth) {
    return { [prefix || 'data']: JSON.stringify(obj) };
  }
  
  Object.keys(obj || {}).forEach(key => {
    // Skip excluded fields
    if (shouldExcludeField(key)) return;
    
    const value = obj[key];
    const newKey = prefix ? `${prefix}_${key}` : key;
    
    if (value === null || value === undefined) {
      flattened[newKey] = '';
    } else if (value instanceof Date || (typeof value === 'string' && isISODateString(value))) {
      // Handle dates and ISO date strings
      const dateValue = value instanceof Date ? value : new Date(value);
      flattened[newKey] = isValidDate(dateValue) ? dateValue.toLocaleDateString() : value;
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      // Handle nested objects
      Object.assign(flattened, flattenObject(value, newKey, depth + 1, maxDepth));
    } else if (Array.isArray(value)) {
      // Handle arrays with better formatting
      if (value.length === 0) {
        flattened[newKey] = '';
      } else if (typeof value[0] === 'object') {
        // For arrays of objects, extract key information
        flattened[newKey + '_count'] = value.length;
        if (value[0].name || value[0].title || value[0].id) {
          flattened[newKey] = value.map(item => 
            item.name || item.title || item.id || JSON.stringify(item)
          ).join(', ');
        } else {
          flattened[newKey] = value.map(item => JSON.stringify(item)).join('; ');
        }
      } else {
        flattened[newKey] = value.join(', ');
      }
    } else if (typeof value === 'boolean') {
      flattened[newKey] = value ? 'Yes' : 'No';
    } else {
      flattened[newKey] = String(value);
    }
  });
  
  return flattened;
};

// Helper to check if string is ISO date format
const isISODateString = (str) => {
  if (typeof str !== 'string') return false;
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
  return isoDateRegex.test(str);
};

// Helper to validate date
const isValidDate = (date) => {
  return date instanceof Date && !isNaN(date);
};

// Export data to CSV with complete data handling
export const exportToCSV = (data, filename = 'export.csv') => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }
  
  try {
    // Prepare data with proper flattening
    const preparedData = prepareDataForExport(data);

    // Detect if this is workspace data - check for both new and legacy formats
    const isWorkspaceData = preparedData[0] && (
      'owner' in preparedData[0] || 'ownerEmail' in preparedData[0] ||
      'Owner' in preparedData[0] || 'Workspace' in preparedData[0] ||
      'ID' in preparedData[0] || 'Usage' in preparedData[0] ||
      'Plan' in preparedData[0] || 'Status' in preparedData[0] ||
      'Created' in preparedData[0]
    );

    // Use appropriate column order
    const preferredOrder = isWorkspaceData ? EXPORT_CONFIG.WORKSPACE_COLUMN_ORDER : EXPORT_CONFIG.USER_COLUMN_ORDER;
    
    // Get all unique headers from all objects
    const allHeaders = new Set();
    preparedData.forEach(row => {
      Object.keys(row).forEach(key => allHeaders.add(key));
    });
    
    // Sort headers with preferred order first
    const headers = Array.from(allHeaders).sort((a, b) => {
      const aIndex = preferredOrder.indexOf(a);
      const bIndex = preferredOrder.indexOf(b);
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });
    
    // Create CSV content with BOM for Excel UTF-8 compatibility
    const BOM = '\uFEFF';
    let csvContent = BOM;
    
    // Add formatted headers
    const formattedHeaders = headers.map(h => formatHeaderName(h));
    csvContent += formattedHeaders.map(h => escapeCSVValue(h)).join(',') + '\n';
    
    // Add data rows
    preparedData.forEach(row => {
      const values = headers.map(header => {
        const value = row[header];
        return escapeCSVValue(value);
      });
      csvContent += values.join(',') + '\n';
    });
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('CSV export error:', error);
    throw error;
  }
};

// Helper function to escape CSV values
const escapeCSVValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  
  const stringValue = String(value);
  
  // Check if value needs to be quoted
  if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"') || stringValue.includes('\r')) {
    // Escape quotes and wrap in quotes
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  
  return stringValue;
};

// Helper function to format header names
const formatHeaderName = (header) => {
  return header
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Unified data preparation function
const prepareDataForExport = (data) => {
  if (!data || data.length === 0) return [];
  
  // Check if this is workspace data (has createdBy field with user info)
  const isWorkspaceData = data[0] && data[0].createdBy && typeof data[0].createdBy === 'object';
  
  return data.map(row => {
    // Handle different data structures
    const cleanRow = { ...row };
    
    // Handle subscription data specifically
    if (cleanRow.customerName !== undefined || cleanRow.customerEmail !== undefined) {
      // This is subscription data with already formatted fields
      cleanRow.name = cleanRow.customerName || 'Unknown';
      cleanRow.email = cleanRow.customerEmail || 'N/A';
      
      // Handle workspace field
      if (cleanRow.workspaceId && typeof cleanRow.workspaceId === 'object') {
        cleanRow.workspace = cleanRow.workspaceId.name || 'N/A';
      }
      
      // Keep other subscription fields
      cleanRow.role = cleanRow.planName || 'N/A';
      cleanRow.status = cleanRow.status || 'Unknown';
      cleanRow.workspaces = cleanRow.creditBalance ? `$${(cleanRow.creditBalance / 100).toFixed(2)}` : '0';
      
    } else if (isWorkspaceData) {
      // Handle backend-provided CSV mapping first (new format from workspaces.controller.js)
      if (cleanRow.ID && cleanRow.Workspace && cleanRow.Owner) {
        // Data is already properly formatted from backend - keep the new column names
        // No additional processing needed
      } else {
        // Fallback for legacy data format - extract owner information
        if (cleanRow.createdBy && typeof cleanRow.createdBy === 'object') {
          cleanRow.Owner = `${cleanRow.createdBy.firstName || ''} ${cleanRow.createdBy.lastName || ''}`.trim() || cleanRow.createdBy.email || 'N/A';
          cleanRow.ownerEmail = cleanRow.createdBy.email || 'N/A';
          delete cleanRow.createdBy;
        }

        // Map workspace name to proper column
        if (cleanRow.name && !cleanRow.Workspace) {
          cleanRow.Workspace = cleanRow.name;
        }

        // Map ID field
        if (cleanRow._id && !cleanRow.ID) {
          cleanRow.ID = cleanRow._id;
        }

        // Extract plan information
        if (cleanRow.currentSubscription && typeof cleanRow.currentSubscription === 'object') {
          cleanRow.Plan = cleanRow.currentSubscription.planName || 'Free';
          cleanRow.Status = cleanRow.currentSubscription.status || 'free';
          delete cleanRow.currentSubscription;
        }

        // Format usage information
        if (cleanRow.memberCount !== undefined && cleanRow.agentCount !== undefined) {
          cleanRow.Usage = `${cleanRow.memberCount} members, ${cleanRow.agentCount} agents`;
        }

        // Format created date
        if (cleanRow.createdAt && !cleanRow.Created) {
          cleanRow.Created = cleanRow.createdAt;
        }
      }
    } else {
      // Ensure critical user fields are properly formatted
      if ('firstName' in cleanRow || 'lastName' in cleanRow) {
        cleanRow.name = `${cleanRow.firstName || ''} ${cleanRow.lastName || ''}`.trim() || 'Unknown';
        // Remove individual name fields after combining
        delete cleanRow.firstName;
        delete cleanRow.lastName;
      }
      
      // Ensure email is available
      if (!cleanRow.email) {
        cleanRow.email = 'N/A';
      }
    }
    
    // Format dates
    ['createdAt', 'updatedAt', 'lastLogin'].forEach(field => {
      if (cleanRow[field]) {
        const date = new Date(cleanRow[field]);
        if (isValidDate(date)) {
          cleanRow[field] = date.toLocaleString();
        }
      }
    });
    
    // Handle status fields - check both 'status' and 'isActive'
    if (!cleanRow.status && 'isActive' in cleanRow) {
      cleanRow.status = cleanRow.isActive ? 'Active' : 'Inactive';
    }
    // Keep existing status field if it exists
    if (cleanRow.status) {
      // Ensure proper capitalization
      cleanRow.status = cleanRow.status.charAt(0).toUpperCase() + cleanRow.status.slice(1).toLowerCase();
    }
    
    // Remove duplicate status indicators and excluded fields
    delete cleanRow.isActive;
    
    // Remove all excluded fields
    EXPORT_CONFIG.EXCLUDED_FIELDS.forEach(field => {
      delete cleanRow[field];
    });
    
    // Flatten the row
    return flattenObject(cleanRow);
  });
};

// Export data to Excel with improved data handling
export const exportToExcel = (data, filename = 'export.xlsx', sheetName = 'Sheet1') => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }
  
  try {
    // Use unified data preparation
    const preparedData = prepareDataForExport(data);
    
    // Detect if this is workspace data
    const isWorkspaceData = preparedData[0] && ('owner' in preparedData[0] || 'ownerEmail' in preparedData[0]);
    
    // Use appropriate column order
    const preferredOrder = isWorkspaceData ? EXPORT_CONFIG.WORKSPACE_COLUMN_ORDER : EXPORT_CONFIG.USER_COLUMN_ORDER;
    
    // Sort and format headers for better readability
    const formattedData = preparedData.map(row => {
      const formatted = {};
      // Add columns in preferred order first
      preferredOrder.forEach(key => {
        if (key in row) {
          formatted[formatHeaderName(key)] = row[key];
        }
      });
      // Add remaining columns
      Object.keys(row).forEach(key => {
        if (!preferredOrder.includes(key)) {
          formatted[formatHeaderName(key)] = row[key];
        }
      });
      return formatted;
    });
    
    // Create a new workbook
    const wb = XLSX.utils.book_new();
    
    // Convert data to worksheet
    const ws = XLSX.utils.json_to_sheet(formattedData);
    
    // Get all headers
    const headers = Object.keys(formattedData[0] || {});
    
    // Auto-size columns
    const cols = headers.map(header => {
      let maxLen = header.length;
      
      formattedData.forEach(row => {
        const val = row[header];
        if (val !== null && val !== undefined) {
          const valStr = String(val);
          maxLen = Math.max(maxLen, valStr.length);
        }
      });
      
      // Set column width with min and max limits
      return { wch: Math.min(Math.max(10, maxLen + 2), 50) };
    });
    
    ws['!cols'] = cols;
    
    // Clean sheet name
    const cleanSheetName = sheetName.substring(0, 31).replace(/[\[\]\*\/\\?:]/g, '');
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, cleanSheetName);
    
    // Write the file properly
    XLSX.writeFile(wb, filename);
    
  } catch (error) {
    console.error('Excel export error:', error);
    
    // Fallback method using Blob
    try {
      const preparedData = prepareDataForExport(data);
      const ws = XLSX.utils.json_to_sheet(preparedData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
      
      // Create blob and download
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (fallbackError) {
      console.error('Fallback Excel export also failed:', fallbackError);
      alert('Failed to export to Excel. Please try again.');
    }
  }
};

// Export multiple sheets to Excel
export const exportMultipleToExcel = (sheets, filename = 'export.xlsx') => {
  if (!sheets || sheets.length === 0) return;
  
  const wb = XLSX.utils.book_new();
  
  sheets.forEach(({ data, name }) => {
    if (data && data.length > 0) {
      const ws = XLSX.utils.json_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, name || 'Sheet');
    }
  });
  
  XLSX.writeFile(wb, filename);
};

// Export data to PDF with enhanced formatting
export const exportToPDF = (data, filename = 'export.pdf', title = 'Data Export', options = {}) => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }
  
  try {
    // Create PDF document
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: 'a4'
    });
    
    let preparedData, headers, processedData;
    
    // Check if we have formatted data from columns
    if (options.formattedData && options.columns) {
      // Use the pre-formatted data with column definitions
      preparedData = options.formattedData;
      
      // Use the column definitions to maintain proper order
      headers = options.columns.map(col => col.name || col.key);
      
      // Process data into array format for autoTable - keep ALL data
      processedData = preparedData.map(row => {
        const processed = [];
        headers.forEach(header => {
          let value = row[header];
          
          // Handle null/undefined
          if (value === null || value === undefined) {
            value = '';
          }
          
          // Convert to string but don't truncate for main PDF export
          value = String(value);
          // Only truncate extremely long values to prevent table overflow
          if (value.length > 100) {
            value = value.substring(0, 97) + '...';
          }
          
          processed.push(value);
        });
        return processed;
      });
      
    } else {
      // Fallback to default data preparation
      preparedData = prepareDataForExport(data);
      
      if (!preparedData || preparedData.length === 0) {
        alert('No data available for PDF export');
        return;
      }
      
      // Get fields in preferred order
      const preferredOrder = EXPORT_CONFIG.USER_COLUMN_ORDER;
      const availableFields = preferredOrder.filter(field => 
        preparedData[0] && field in preparedData[0]
      );
      
      // If no preferred fields, use what's available
      if (availableFields.length === 0) {
        const allFields = Object.keys(preparedData[0] || {});
        availableFields.push(...allFields.slice(0, 8));
      }
      
      // Create headers
      headers = availableFields.map(field => formatHeaderName(field));
      
      // Process data
      processedData = preparedData.map(row => {
        const processed = [];
        availableFields.forEach(field => {
          let value = row[field];
          
          if (value === null || value === undefined) {
            value = '';
          } else if (typeof value === 'object') {
            value = JSON.stringify(value);
          } else {
            value = String(value);
          }
          
          if (value.length > 40) {
            value = value.substring(0, 37) + '...';
          }
          
          processed.push(value);
        });
        return processed;
      });
    }
    
    // Add title
    doc.setFontSize(16);
    const isWorkspaceExport = headers && (headers.includes('Workspace Name') || headers.includes('Owner') || headers.includes('Owner Email'));
    const exportTitle = isWorkspaceExport ? 'Workspace Export' : (title || 'Data Export');
    doc.text(exportTitle, 40, 40);
    
    // Add date
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 40, 60);
    
    // Generate table using autoTable - ensure ALL data is included
    doc.autoTable({
      head: [headers],
      body: processedData,
      startY: 80,
      margin: { horizontal: 40 },
      styles: {
        fontSize: 8,
        cellPadding: 3,
        overflow: 'linebreak',
        valign: 'middle'
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold',
        lineColor: [200, 200, 200],
        lineWidth: 0.5,
        cellPadding: 6
      },
      alternateRowStyles: {
        fillColor: [245, 248, 250]
      },
      columnStyles: {
        // Auto width for all columns with max width to prevent overflow
        0: { cellWidth: 'auto', minCellWidth: 20 },
        1: { cellWidth: 'auto', minCellWidth: 30 },
        2: { cellWidth: 'auto', minCellWidth: 15 },
        3: { cellWidth: 'auto', minCellWidth: 15 },
        4: { cellWidth: 'auto', minCellWidth: 20 },
        5: { cellWidth: 'auto', minCellWidth: 20 },
        6: { cellWidth: 'auto', minCellWidth: 20 },
        7: { cellWidth: 'auto', minCellWidth: 20 }
      },
      // Add page break handling
      showHead: 'everyPage',
      pageBreak: 'auto',
      tableWidth: 'auto'
    });
    
    // Add footer with total records - use the actual formatted data length for accurate count
    const finalY = doc.lastAutoTable?.finalY || 80;
    doc.setFontSize(10);
    const totalCount = options.formattedData ? options.formattedData.length : processedData.length;
    doc.text(`Total Records: ${totalCount}`, 40, finalY + 20);
    
    // Save the PDF
    doc.save(filename);
    
  } catch (error) {
    console.error('PDF Export Error:', error);
    console.error('Error details:', error.message, error.stack);
    
    // Detailed fallback with better data display
    try {
      const doc = new jsPDF('landscape');
      
      // Use formatted data if available, otherwise prepare the data
      let dataToUse;
      if (options.formattedData) {
        dataToUse = options.formattedData;
      } else {
        dataToUse = prepareDataForExport(data);
      }
      
      // Check if it's workspace data
      const isWorkspaceData = dataToUse[0] && ('owner' in dataToUse[0] || 'ownerEmail' in dataToUse[0] || 
                                                'Owner' in dataToUse[0] || 'Owner Email' in dataToUse[0] ||
                                                'Workspace Name' in dataToUse[0]);
      
      // Add title
      doc.setFontSize(14);
      const exportTitle = isWorkspaceData ? 'Workspace Export' : (title || 'Data Export');
      doc.text(exportTitle, 20, 20);
      
      // Add date
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 30);
      
      // Create simple table-like structure
      doc.setFontSize(9);
      let yPosition = 45;
      
      // Add appropriate headers
      doc.setFont(undefined, 'bold');
      doc.setFontSize(8);
      if (isWorkspaceData) {
        doc.text('Workspace | Owner | Email | Plan | Status | Users | Agents | Created', 20, yPosition);
      } else {
        // Check if this is subscription data
        const hasCustomerFields = dataToUse[0] && (dataToUse[0]['Customer Name'] || dataToUse[0].customerName);
        if (hasCustomerFields) {
          doc.text('Customer | Email | Workspace | Plan | Status | Credits | Created', 20, yPosition);
        } else {
          doc.text('Name | Email | Role | Status | Workspaces | Created', 20, yPosition);
        }
      }
      yPosition += 5;
      doc.line(20, yPosition, 280, yPosition); // Horizontal line (landscape)
      yPosition += 10;
      
      // Add data rows - show ALL records, handle pagination if needed
      doc.setFont(undefined, 'normal');
      doc.setFontSize(7);
      const actualTotal = dataToUse.length;
      
      dataToUse.forEach((row, index) => {
        let text;
        
        if (isWorkspaceData) {
          // Workspace data - check both raw field names and formatted column names
          const workspace = row['Workspace Name'] || row.name || row.Name || 'Unknown';
          const owner = row['Owner'] || row.owner || 'N/A';
          const email = row['Owner Email'] || row.ownerEmail || 'N/A';
          const plan = row['Plan'] || row.plan || 'Free';
          const status = row['Status'] || row.status || 'Unknown';
          const users = row['Users'] || row.memberCount || row['Member Count'] || '0';
          const agents = row['Agents'] || row.agentCount || row['Agent Count'] || '0';
          const created = row['Created'] || row.createdAt || row['Created At'] || '';
          
          // Format to fit
          const shortWorkspace = workspace.length > 20 ? workspace.substring(0, 17) + '...' : workspace;
          const shortOwner = owner.length > 15 ? owner.substring(0, 12) + '...' : owner;
          const shortEmail = email.length > 20 ? email.substring(0, 17) + '...' : email;
          
          text = `${shortWorkspace} | ${shortOwner} | ${shortEmail} | ${plan} | ${status} | ${users} | ${agents} | ${created}`;
        } else {
          // User/Subscription data - check for subscription fields first
          const customerName = row['Customer Name'] || row.customerName;
          const customerEmail = row['Customer Email'] || row.customerEmail;
          
          // If we have customer fields, this is subscription data
          if (customerName !== undefined || customerEmail !== undefined) {
            const name = customerName || row.Name || row.name || 'Unknown';
            const email = customerEmail || row.Email || row.email || 'No email';
            const workspace = row['Workspace'] || row.workspace || row.workspaceId?.name || 'N/A';
            const plan = row['Plan'] || row.planName || 'N/A';
            const status = row['Status'] || row.status || 'Unknown';
            const credits = row['Credits'] || row.creditBalance || '0';
            const created = row['Created'] || row.createdAt || row['Created At'] || '';
            
            // Format to fit
            const shortName = name.length > 20 ? name.substring(0, 17) + '...' : name;
            const shortEmail = email.length > 25 ? email.substring(0, 22) + '...' : email;
            const shortWorkspace = workspace.length > 15 ? workspace.substring(0, 12) + '...' : workspace;
            
            text = `${shortName} | ${shortEmail} | ${shortWorkspace} | ${plan} | ${status} | ${credits} | ${created}`;
          } else {
            // Regular user data
            const firstName = row['First Name'] || row.firstName || '';
            const lastName = row['Last Name'] || row.lastName || '';
            const name = row.Name || row.name || `${firstName} ${lastName}`.trim() || 'Unknown';
            const email = row.Email || row.email || 'No email';
            const role = row.Role || row.role || 'user';
            const status = row.Status || row.status || (row.isActive ? 'Active' : 'Inactive') || 'Unknown';
            const workspaces = row.Workspaces || row.workspaces || '0';
            const created = row.Created || row.createdAt || row['Created At'] || '';
            
            // Format name to fit
            const shortName = name.length > 20 ? name.substring(0, 17) + '...' : name;
            const shortEmail = email.length > 25 ? email.substring(0, 22) + '...' : email;
            
            text = `${shortName} | ${shortEmail} | ${role} | ${status} | ${workspaces} | ${created}`;
          }
        }
        
        doc.text(text, 20, yPosition);
        yPosition += 7;
        
        // Add new page if needed (check before reaching bottom)
        if (yPosition > 185) {  // Leave space for footer
          doc.addPage();
          yPosition = 20;
          // Re-add headers on new page
          doc.setFont(undefined, 'bold');
          doc.setFontSize(8);
          if (isWorkspaceData) {
            doc.text('Workspace | Owner | Email | Plan | Status | Users | Agents | Created', 20, yPosition);
          } else {
            const hasCustomerFields = dataToUse[0] && (dataToUse[0]['Customer Name'] || dataToUse[0].customerName);
            if (hasCustomerFields) {
              doc.text('Customer | Email | Workspace | Plan | Status | Credits | Created', 20, yPosition);
            } else {
              doc.text('Name | Email | Role | Status | Workspaces | Created', 20, yPosition);
            }
          }
          yPosition += 5;
          doc.line(20, yPosition, 280, yPosition);
          yPosition += 10;
          doc.setFont(undefined, 'normal');
          doc.setFontSize(7);
        }
      });
      
      // Add total count - use the actual total, not just what's displayed
      yPosition += 10;
      doc.setFont(undefined, 'bold');
      doc.text(`Total Records: ${actualTotal}`, 20, yPosition);
      
      doc.save(filename);
    } catch (fallbackError) {
      console.error('PDF Fallback Error:', fallbackError);
      alert('Unable to generate PDF. Please use CSV or Excel export instead.');
    }
  }
};

// Helper function to determine key fields for PDF
const determineKeyFields = (data) => {
  if (!data || data.length === 0) return [];
  
  const firstRow = data[0];
  const allFields = Object.keys(firstRow).filter(field => !shouldExcludeField(field));
  
  // Priority fields for display
  const priorityFields = EXPORT_CONFIG.PDF.PRIORITY_FIELDS.concat([
    'title', 'description', 'date'
  ]);
  
  // Select fields based on priority
  const selectedFields = [];
  priorityFields.forEach(field => {
    if (allFields.includes(field)) {
      selectedFields.push(field);
    }
  });
  
  // Add remaining fields if space permits
  allFields.forEach(field => {
    if (!selectedFields.includes(field) && selectedFields.length < EXPORT_CONFIG.PDF.MAX_COLUMNS) {
      selectedFields.push(field);
    }
  });
  
  return selectedFields;
};

// Export table with custom formatting
export const exportFormattedData = (data, columns, format, filename) => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }
  
  // Apply column formatting but keep original data for PDF
  const formattedData = data.map(row => {
    const formattedRow = {};
    columns.forEach(col => {
      let value = row[col.key];
      
      // Apply formatter if provided
      if (col.formatter && typeof col.formatter === 'function') {
        try {
          value = col.formatter(value, row);
        } catch (e) {
          console.warn(`Formatter error for ${col.key}:`, e);
          value = row[col.key];
        }
      }
      
      // Ensure value is not undefined or null
      if (value === undefined || value === null) {
        // Try to get a fallback value based on the column key
        if (col.key === 'customerName' && row.customerName) {
          value = row.customerName;
        } else if (col.key === 'customerEmail' && row.customerEmail) {
          value = row.customerEmail;
        } else {
          value = '';
        }
      }
      
      // Use display name or key
      formattedRow[col.name || col.key] = value;
    });
    return formattedRow;
  });
  
  // Export based on format
  switch (format) {
    case 'csv':
      exportToCSV(formattedData, filename);
      break;
    case 'excel':
      exportToExcel(formattedData, filename);
      break;
    case 'pdf':
      // For PDF, pass the formatted data (which has the correct count) instead of original data
      exportToPDF(formattedData, filename, filename.replace(/\.[^/.]+$/, ''), { 
        columns: columns,
        formattedData: formattedData 
      });
      break;
    default:
      exportToCSV(formattedData, filename);
  }
};

// Utility to download blob
export const downloadBlob = (blob, filename) => {
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Export HTML table to Excel
export const exportTableToExcel = (tableId, filename = 'table-export.xlsx') => {
  const table = document.getElementById(tableId);
  if (!table) return;
  
  const wb = XLSX.utils.table_to_book(table);
  XLSX.writeFile(wb, filename);
};

// Batch export with progress callback
export const batchExport = async (data, format, options = {}) => {
  const { batchSize = 1000, onProgress, filename = 'batch-export' } = options;
  
  if (!data || data.length === 0) return;
  
  const totalBatches = Math.ceil(data.length / batchSize);
  const batches = [];
  
  for (let i = 0; i < totalBatches; i++) {
    const start = i * batchSize;
    const end = Math.min(start + batchSize, data.length);
    const batch = data.slice(start, end);
    
    if (onProgress) {
      onProgress({
        current: i + 1,
        total: totalBatches,
        percentage: Math.round(((i + 1) / totalBatches) * 100)
      });
    }
    
    batches.push(batch);
  }
  
  // For large datasets, combine batches
  const combinedData = batches.flat();
  
  switch (format) {
    case 'csv':
      exportToCSV(combinedData, `${filename}.csv`);
      break;
    case 'excel':
      exportToExcel(combinedData, `${filename}.xlsx`);
      break;
    case 'pdf':
      // For PDF, export all data - autoTable handles pagination
      exportToPDF(combinedData, `${filename}.pdf`, options.title);
      break;
  }
};